import React, { useState, useRef, useEffect } from "react";
import "./ContactSection.css";
import { waiaLogo } from "@/assets";
import { sendContactForm, ContactPayload } from "@/services/contactService";
import { getRecaptchaToken } from "@/services/recaptcha";
import { useLocale } from "@/i18n/useLocale";

// El backend limita `message` a 3000 caracteres, y ahi viajan tambien la etapa
// y el presupuesto. Se reserva margen para ese prefijo asi el limite se avisa
// mientras se escribe, en vez de rebotar con un error generico al enviar.
const IDEA_MAX_LENGTH = 2800;

// Tiempo de inactividad antes de capturar un lead parcial en segundo plano
// (ver capturePartialLead mas abajo).
const PARTIAL_CAPTURE_DEBOUNCE_MS = 1200;

interface ContactWizardProps {
  onSuccess: () => void;
}

const ContactWizard: React.FC<ContactWizardProps> = ({ onSuccess }) => {
  const { t, locale } = useLocale();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  interface FormData {
    busqueda: string[];
    idea: string;
    etapa: string;
    presupuesto: string;
    nombre: string;
    email: string;
    telefono: string;
    country: string;
  }

  const [formData, setFormData] = useState<FormData>({
    busqueda: [],
    idea: "",
    etapa: "",
    presupuesto: "",
    nombre: "",
    email: "",
    telefono: "",
    country: t.wizard.countries[0],
  });
  const selectRef = useRef<HTMLDivElement>(null);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

  // selectRef estaba asignado al div pero nunca se leia: faltaba este handler,
  // asi que el desplegable de paises no se cerraba al clickear afuera.
  useEffect(() => {
    if (!isSelectOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsSelectOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsSelectOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isSelectOpen]);

  // Se ordena con el comparador del idioma: en español "España" va antes que
  // "Estados Unidos", y un sort() a secas no respeta los acentos.
  const countries = [...t.wizard.countries].sort((a, b) =>
    a.localeCompare(b, locale),
  );

  const isContactStepValid = () =>
    !!formData.nombre.trim() &&
    !!formData.email.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
    !!formData.telefono.trim() &&
    !!formData.country;

  // Se manda una sola vez por sesion de formulario, disparada por lo que
  // ocurra primero: el debounce de abajo (si el visitante deja de escribir y
  // no avanza) o el paso a la siguiente pregunta (handleNext). El guard es
  // sincrono -se marca antes del await- para que ambos disparadores no manden
  // el mismo lead dos veces si coinciden.
  const partialCapturedRef = useRef(false);

  const capturePartialLead = () => {
    if (partialCapturedRef.current || !isContactStepValid()) return;
    partialCapturedRef.current = true;

    (async () => {
      try {
        if (!recaptchaSiteKey) return;

        const token = await getRecaptchaToken(recaptchaSiteKey, {
          load: t.wizard.errRecaptchaLoad,
          unavailable: t.wizard.errRecaptchaUnavailable,
        });

        if (!token) return;

        // Mismo backend que el envio completo, marcado para distinguirlo en
        // el mail: dejo sus datos de contacto pero no llego a terminar (o
        // todavia no termino) las preguntas de calificacion.
        await sendContactForm({
          name: formData.nombre,
          email: formData.email,
          phone: formData.telefono,
          country: formData.country,
          company: "",
          service: "Lead parcial (datos de contacto sin calificar)",
          message:
            "Completo sus datos de contacto en el formulario largo pero aun no llego a responder las preguntas de calificacion (o no lo hizo). Contactar para calificar manualmente.",
          recaptcha_token: token,
        });
      } catch {
        // Best-effort: si falla, no se interrumpe ni se avisa al visitante.
        // El envio final del wizard sigue siendo la via principal.
      }
    })();
  };

  // Dispara la captura en segundo plano en cuanto los datos de contacto
  // quedan completos y el visitante deja de escribir, sin esperar a que
  // avance de paso -para no perder el lead si cierra la pestaña o se va sin
  // completar el resto del formulario.
  useEffect(() => {
    if (step !== 1 || !isContactStepValid()) return;

    const timer = setTimeout(capturePartialLead, PARTIAL_CAPTURE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, formData.nombre, formData.email, formData.telefono, formData.country]);

  const handleNext = () => {
    if (step === 1) capturePartialLead();

    if (step < 5) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    const fieldMap: Record<string, keyof FormData> = {
      "given-name": "nombre",
      email: "email",
      tel: "telefono",
      idea: "idea",
    };

    const fieldName = fieldMap[name] || (name as keyof FormData);
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleOptionSelect = (name: keyof FormData, value: string) => {
    if (name === "busqueda") {
      setFormData((prev) => {
        const current = prev.busqueda;
        const index = current.indexOf(value);
        if (index > -1) {
          return { ...prev, busqueda: current.filter((v) => v !== value) };
        } else {
          return { ...prev, busqueda: [...current, value] };
        }
      });
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setLoading(true);
    setStatus(null);

    try {
      if (!recaptchaSiteKey) {
        setStatus({
          success: false,
          message: t.wizard.errRecaptchaKey,
        });
        return;
      }

      const token = await getRecaptchaToken(recaptchaSiteKey, {
        load: t.wizard.errRecaptchaLoad,
        unavailable: t.wizard.errRecaptchaUnavailable,
      });

      if (!token) {
        setStatus({
          success: false,
          message: t.wizard.errRecaptchaVerify,
        });
        setLoading(false);
        return;
      }

      const payload: ContactPayload = {
        name: formData.nombre,
        email: formData.email,
        phone: formData.telefono,
        country: formData.country,
        // El formulario ya no pide empresa. El backend la tiene como opcional,
        // asi que viaja vacia en vez de romper el contrato del endpoint.
        company: "",
        service: formData.busqueda.join(", "),
        message: `Etapa: ${formData.etapa}\nPresupuesto: ${formData.presupuesto}\nIdea: ${formData.idea}`,
        recaptcha_token: token,
      };

      const result = await sendContactForm(payload);

      if (result.success) {
        onSuccess();
      } else {
        setStatus({ success: false, message: result.message });
      }
    } catch {
      setStatus({
        success: false,
        message: t.wizard.errUnexpected,
      });
    } finally {
      setLoading(false);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return isContactStepValid();
      case 2:
        return formData.busqueda.length > 0;
      case 3:
        return !!formData.idea.trim();
      case 4:
        return !!formData.etapa;
      case 5:
        return !!formData.presupuesto;
      default:
        return false;
    }
  };

  const stepHeadingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (step > 1) {
      stepHeadingRef.current?.focus();
    }
  }, [step]);

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="wizard-step">
            <h2 tabIndex={-1} ref={stepHeadingRef} className="wizard-step__title">
              {t.wizard.q5Title}
            </h2>
            <p className="wizard-step__subtitle">{t.wizard.q5Subtitle}</p>
            {/* Cuatro campos en dos filas de dos. Al sacar apellido y empresa
                quedaban tres filas con un solo campo cada una, cada uno a media
                caja y con el resto vacio. */}
            <div className="wizard-form">
              <div className="wizard-form__row">
                <div className="wizard-field">
                  <input
                    type="text"
                    name="given-name"
                    id="nombre"
                    autoComplete="name"
                    placeholder=" "
                    value={formData.nombre}
                    onChange={handleChange}
                  />
                  <label htmlFor="nombre">{t.wizard.firstName}</label>
                </div>
                <div className="wizard-field">
                  <input
                    type="email"
                    name="email"
                    id="email"
                    autoComplete="email"
                    placeholder=" "
                    value={formData.email}
                    onChange={handleChange}
                  />
                  <label htmlFor="email">{t.wizard.email}</label>
                </div>
              </div>
              <div className="wizard-form__row">
                <div className="wizard-field">
                  {/* type="tel" y no "text": levanta el teclado numerico en
                      mobile, que es donde se completa casi todo. */}
                  <input
                    type="tel"
                    name="tel"
                    id="telefono"
                    autoComplete="tel"
                    placeholder=" "
                    value={formData.telefono}
                    onChange={handleChange}
                  />
                  <label htmlFor="telefono">{t.wizard.phone}</label>
                </div>
                <div
                  className={`wizard-select wizard-field ${isSelectOpen ? "is-active" : ""} ${formData.country ? "has-value" : ""}`}
                  ref={selectRef}
                >
                  <div
                    id="wizard-country-trigger"
                    role="combobox"
                    aria-label={t.wizard.country}
                    aria-expanded={isSelectOpen}
                    aria-haspopup="listbox"
                    aria-controls="wizard-country-options"
                    tabIndex={0}
                    className="wizard-select__trigger"
                    onClick={() => setIsSelectOpen(!isSelectOpen)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setIsSelectOpen(!isSelectOpen);
                      } else if (e.key === "ArrowDown") {
                        e.preventDefault();
                        if (!isSelectOpen) setIsSelectOpen(true);
                      }
                    }}
                  >
                    {formData.country}
                  </div>
                  <label htmlFor="wizard-country-trigger">{t.wizard.country}</label>
                  {isSelectOpen && (
                    <div id="wizard-country-options" role="listbox" className="wizard-select__options">
                      {countries.map((c) => (
                        <div
                          key={c}
                          role="option"
                          aria-selected={formData.country === c}
                          tabIndex={0}
                          className={`wizard-select__option ${formData.country === c ? "is-selected" : ""}`}
                          onClick={() => {
                            handleOptionSelect("country", c);
                            setIsSelectOpen(false);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleOptionSelect("country", c);
                              setIsSelectOpen(false);
                            }
                          }}
                        >
                          {c}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="wizard-step">
            <h2 tabIndex={-1} ref={stepHeadingRef} className="wizard-step__title">
              {t.wizard.q1Title}
            </h2>
            <p className="wizard-step__subtitle">{t.wizard.q1Subtitle}</p>
            <div className="wizard-options" role="group" aria-label={t.wizard.q1Title}>
              {t.wizard.q1Options.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  aria-pressed={formData.busqueda.includes(opt)}
                  className={`wizard-option ${formData.busqueda.includes(opt) ? "is-selected" : ""}`}
                  onClick={() => handleOptionSelect("busqueda", opt)}
                >
                  {opt}
                  {formData.busqueda.includes(opt) && (
                    <span className="wizard-option__check" aria-hidden="true">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        );
      case 3:
        return (
          <div className="wizard-step">
            <h2 tabIndex={-1} ref={stepHeadingRef} className="wizard-step__title">
              {t.wizard.q2Title}
            </h2>
            <p className="wizard-step__subtitle">{t.wizard.q2Subtitle}</p>
            <label htmlFor="wizard-idea" className="sr-only">
              {t.wizard.q2Title}
            </label>
            <textarea
              id="wizard-idea"
              name="idea"
              className="wizard-textarea"
              placeholder={t.wizard.q2Placeholder}
              maxLength={IDEA_MAX_LENGTH}
              value={formData.idea}
              onChange={handleChange}
            />
            <div className="wizard-textarea__counter">
              {formData.idea.length} / {IDEA_MAX_LENGTH}
            </div>
          </div>
        );
      case 4:
        return (
          <div className="wizard-step">
            <h2 tabIndex={-1} ref={stepHeadingRef} className="wizard-step__title">
              {t.wizard.q3Title}
            </h2>
            <p className="wizard-step__subtitle">{t.wizard.q3Subtitle}</p>
            <div className="wizard-options" role="radiogroup" aria-label={t.wizard.q3Title}>
              {t.wizard.q3Options.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  role="radio"
                  aria-checked={formData.etapa === opt}
                  className={`wizard-option ${formData.etapa === opt ? "is-selected" : ""}`}
                  onClick={() => handleOptionSelect("etapa", opt)}
                >
                  {opt}
                  {formData.etapa === opt && (
                    <span className="wizard-option__check" aria-hidden="true">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        );
      case 5:
        return (
          <div className="wizard-step">
            <h2 tabIndex={-1} ref={stepHeadingRef} className="wizard-step__title">
              {t.wizard.q4Title}
            </h2>
            <p className="wizard-step__subtitle">{t.wizard.q4Subtitle}</p>
            <div className="wizard-options" role="radiogroup" aria-label={t.wizard.q4Title}>
              {t.wizard.q4Options.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  role="radio"
                  aria-checked={formData.presupuesto === opt}
                  className={`wizard-option wizard-option--radio ${formData.presupuesto === opt ? "is-selected" : ""}`}
                  onClick={() => handleOptionSelect("presupuesto", opt)}
                >
                  <div className="wizard-option__radio-circle" aria-hidden="true" />
                  {opt}
                  {formData.presupuesto === opt && (
                    <span className="wizard-option__check" aria-hidden="true">✓</span>
                  )}
                </button>
              ))}
            </div>
            {status && !status.success && (
              <div className={`wizard-status is-error`}>{status.message}</div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="contact-wizard">
      <div className="contact-wizard__header">
        <img src={waiaLogo} loading="lazy" alt="Waia Logo" title="WAIA" className="contact-wizard__logo" />
        <div className="contact-wizard__step-indicator">
          {t.wizard.step} {step} {t.wizard.of} 5
        </div>
      </div>

      <div className="contact-wizard__content">{renderStep()}</div>

      <div className="contact-wizard__footer">
        <div className="contact-wizard__actions">
          {step > 1 && (
            <button
              className="wizard-btn wizard-btn--secondary"
              onClick={handleBack}
            >
              {t.wizard.back}
            </button>
          )}
          <div className="wizard-progress">
            <div
              className="wizard-progress__bar"
              style={{ width: `${(step / 5) * 100}%` }}
            />
          </div>
          <button
            type="button"
            className={`wizard-btn wizard-btn--primary ${loading ? "is-loading" : ""}`}
            onClick={handleNext}
            disabled={!isStepValid() || loading}
          >
            {loading ? t.wizard.sending : step === 5 ? t.wizard.send : t.wizard.next}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactWizard;
